import React, { useState } from 'react';
import PaymentModal from '../../../components/PaymentModal/PaymentModal';

type Period = 'monthly' | 'biannual' | 'annual';

// --- Dados Mockados ---
const plans = [
  {
    name: 'Simples',
    isPopular: false,
    features: [
      'Análise de até 10 empresas',
      'Alertas via WhatsApp',
      'Dashboard com visão geral',
      'Suporte por email',
    ],
    prices: {
      monthly: { price: '19,90', per: 'mês' },
      biannual: { price: '99,90', per: 'semestre' },
      annual: { price: '189,90', per: 'ano' },
    },
  },
  {
    name: 'Plus',
    isPopular: true,
    features: [
      'Análise de até 50 empresas',
      'Alertas via WhatsApp e Email',
      'Dashboard completo com insights',
      'Acompanhamento de carteira',
      'Suporte prioritário via chat',
    ],
    prices: {
      monthly: { price: '39,90', per: 'mês' },
      biannual: { price: '199,90', per: 'semestre' },
      annual: { price: '379,90', per: 'ano' },
    },
  },
  {
    name: 'Pro',
    isPopular: false,
    features: [
      'Análise de empresas ilimitadas',
      'Alertas via WhatsApp, Email e SMS',
      'Dashboard Pro com IA preditiva',
      'Suporte dedicado 24/7',
      'Acesso antecipado a novas features',
      'Consultoria mensal com especialista',
    ],
    prices: {
      monthly: { price: '79,90', per: 'mês' },
      biannual: { price: '399,90', per: 'semestre' },
      annual: { price: '749,90', per: 'ano' },
    },
  },
];

const mockPixData = {
  encodedImage: "iVBORw0KGgoAAAANSUhEUgAAAcIAAAHCAQAAAABUY/ToAAADHklEQVR4Xu2TS47bQBBDvfP9b5RjaeeIjyyNE2CgYDYpAyzZrfrwUYvufrx+GL8ef3f+NUreRcm7KHkXJe+i5F38N/J4EE81lD8P0jN/XuWZvw7KKEvuJc08NVVB5kIcUqhYspZcTE7Xynex+upKNnnJzyDpTI2Hl7H84zyUXE/mIHgETKSUvORnkK/pItRiaS4zC3WUJVeTEMzvnihLfvssIK/QnvuXXddx8O5rOglRci3J/p4AkMvZdYGHFwz9lNxOzjAwQXtW62NScj+prpc4yMMJgAuJjZZcSxoalvtsgRchsU6mKLmWlEAasGvTITgFL3sEklfJzSTFIYR5TOYfmrAFVcm1pNQKsyn1lm70lHYDLbmWFEtPEsnhNMYVmBXTkutJGsKeSL4M/JLrzP2BkstJ5TLw/DoFV2nD+YSGJdeSIkKPB6IASr4YjaQpuZYcQtvut1IOAC8PRT7gcS+5ljyUnbXekiP5k7CDdDRKLidpS3mw11ik91aj8IdKLiY1fEhil1R0Uvtxy2jJxeTZDCUhZTqoxk6dkp9Aosl0jsF00qLIU3IzKVSVj4GUsWNAbcd0mJdcS2rHHYwxiIlIu5LaFrTkWpKwHpEzFehpIvefsuReMrus9hyGORY4WBJBlCW3k7Px+tsk9JPd50n+KrmZ9ASUpmWC/wIteqEquZYkM6TXZBczblOW3E0GOJUv3WsJuNSurL/u+3iVXEt6QFsVK00cQKB4BSy5l6R5tQJcGtVnEQ/50i65lwwiQkMIctRywSAf4FslF5NpfQ2FoKdwSYdjAVxyL3lOVEJJcBkZe69xElNyMRnE281Mv8fXyXgynoHQkotJadzzMuVlQyc2JZeTAeccJJtzkaGSCEruJifUmU2Xlso6Ve54WnIx+b7HVh7aeOVxyenw1M2Se8lrTDJeOOiFNDZakJVcTJ6K93Og2iENbR0Nd2Jf8hPIt+HBlmuCcnTfnYSSG8kn11f3F9AVte83Yt4lF5NpzRwQNFJmY+d2yb0k93n60Y6Slm90pr7rJdeSP6qST1XyqUo+VcmnKvlUH0b+Bv8CiKSlS0HTAAAAAElFTkSuQmCC",
  payload: "00020101021226820014br.gov.bcb.pix2560pix-h.asaas.com/qr/cobv/c68d6543-3549-4e0b-a500-8c51b0de88b75204000053039865802BR5925ASAAS GESTAO FINANCEIRA S6009Joinville61088922730162070503***6304980A",
};

const mockBankSlipData = {
  identificationField: "46191110000000000000011730744015113460000002500",
  bankSlipUrl: "https://sandbox.asaas.com/b/pdf/4jtmxro9mlqf3bfk",
};

// --- Funções Mock ---
const fetchPaymentDetails = async (planName: string, period: Period) => {
  console.log(`Buscando detalhes de pagamento para ${planName} (${period})...`);
  // Simula um atraso da rede
  await new Promise(resolve => setTimeout(resolve, 1000));
  
  return {
    pix: mockPixData,
    bankSlip: mockBankSlipData,
  };
};

const SubscriptionPage: React.FC = () => {
  const [activePlanForPeriodSelection, setActivePlanForPeriodSelection] = useState<string | null>(null);
  const [selectedPeriod, setSelectedPeriod] = useState<Period>('annual');
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [paymentData, setPaymentData] = useState(null);
  const [currentPlan, setCurrentPlan] = useState<{name: string, price: string, period: string} | null>(null);

  const handlePlanClick = (planName: string) => {
    setActivePlanForPeriodSelection(planName);
  };

  const handlePeriodSelect = (period: Period) => {
    setSelectedPeriod(period);
  };

  const handleCloseOverlay = (e: React.MouseEvent) => {
    e.stopPropagation();
    setActivePlanForPeriodSelection(null);
  };

  const handleSubscribeClick = async (e: React.MouseEvent, planName: string) => {
    e.stopPropagation();
    
    // Define os detalhes do plano atual para passar para o modal
    const plan = plans.find(p => p.name === planName);
    if (!plan) return;
    
    setCurrentPlan({
      name: plan.name,
      price: plan.prices[selectedPeriod].price,
      period: plan.prices[selectedPeriod].per,
    });

    // Abre o modal e mostra um spinner
    setIsPaymentModalOpen(true);
    setPaymentData(null); // Limpa dados antigos

    // "Busca" os novos dados
    const data = await fetchPaymentDetails(planName, selectedPeriod);
    setPaymentData(data as any);
  };

  const handleCloseModal = () => {
    setIsPaymentModalOpen(false);
    setPaymentData(null);
    setCurrentPlan(null);
  }

  return (
    <>
      <div className="max-w-6xl mx-auto px-4 py-8 md:px-6 md:py-10">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Planos de Assinatura</h1>
          <p className="text-xl text-gray-600">Escolha o plano ideal para seus investimentos e mude a qualquer momento.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 items-stretch">
          {plans.map(plan => {
            const isOverlayActive = activePlanForPeriodSelection === plan.name;
            return (
              <div
                key={plan.name}
                className={`flex flex-col bg-white rounded-xl shadow-md p-8 border-2 border-gray-200 relative transition-all duration-300 cursor-pointer overflow-hidden
                  ${isOverlayActive ? 'border-blue-700 shadow-lg scale-102' : 'hover:-translate-y-1 hover:shadow-lg'}`}
                onClick={() => handlePlanClick(plan.name)}
              >
                {plan.isPopular && <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-br from-blue-700 to-blue-900 text-white px-6 py-2 rounded-full text-sm font-semibold shadow-md z-10">Mais Popular</div>}
                
                <div className={`flex flex-col flex-grow transition-filter duration-300 ${isOverlayActive ? 'filter blur-sm grayscale-50 pointer-events-none select-none' : ''}`}>
                  <h3 className="text-3xl font-semibold text-blue-700 mb-6 text-center">{plan.name}</h3>
                  <ul className="list-none mb-8 p-0 flex-grow">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="flex items-start gap-3 mb-4 text-gray-900">
                        <span className="text-xl text-green-500 flex-shrink-0">✅</span>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {isOverlayActive && (
                  <div className={`absolute inset-0 bg-white bg-opacity-90 rounded-xl flex flex-col items-center p-4 z-20 transition-all duration-300 ${isOverlayActive ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'}`}>
                    <button className="absolute top-4 right-4 bg-transparent border-none text-2xl cursor-pointer text-gray-600 hover:text-red-500 transition-colors duration-200" onClick={handleCloseOverlay}>
                      &times;
                    </button>
                    <div className="w-full max-w-xs mt-auto mb-auto">
                      <h4 className="text-xl text-gray-900 text-center mb-6">Escolha a periodicidade</h4>
                      <div className="flex flex-col gap-4 mb-6">
                        {(Object.keys(plan.prices) as Period[]).map(period => (
                          <button
                            key={period}
                            className={`w-full p-4 rounded-lg border-2 border-gray-200 bg-white text-gray-900 text-base font-medium cursor-pointer transition-all duration-200 flex justify-between items-center
                              ${selectedPeriod === period ? 'border-green-500 bg-green-50 font-semibold' : 'hover:border-blue-700 hover:bg-blue-50'}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              handlePeriodSelect(period);
                            }}
                          >
                            <span className="capitalize">
                              {period === 'monthly' ? 'Mensal' : period === 'biannual' ? 'Semestral' : 'Anual'}
                            </span>
                            <span className="font-bold text-blue-700">
                              R$ {plan.prices[period].price} / {plan.prices[period].per}
                            </span>
                          </button>
                        ))}
                      </div>
                      <button 
                        className="w-full p-4 text-lg font-semibold rounded-lg transition-all duration-200 cursor-pointer bg-blue-700 text-white hover:bg-blue-800"
                        onClick={(e) => handleSubscribeClick(e, plan.name)}
                      >
                        Assinar {plan.name}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
      
      <PaymentModal 
        isOpen={isPaymentModalOpen}
        onClose={handleCloseModal}
        paymentData={paymentData}
        planName={currentPlan?.name || ''}
        planPrice={currentPlan?.price || ''}
        planPeriod={currentPlan?.period || ''}
      />
    </>
  );
};

export default SubscriptionPage;
